import { useState } from 'react';
import { Sparkles, CreditCard, Loader2, ChevronRight, Mail, Clock, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { stripeApi, type BillingStatus, type FeatureFlags, type Business, type User } from '@/lib/api';
import { EmailVerificationModal } from './Modals/EmailVerificationModal';

interface HeaderAlertsProps {
  billingStatus: BillingStatus | null;
  featureFlags: FeatureFlags | null;
  currentBusiness: Business | null;
  user: User | null;
  onRefreshUser: () => Promise<void>;
  onTrialExpiredClick?: () => void;
}

export function HeaderAlerts({ billingStatus, featureFlags, currentBusiness, user, onRefreshUser, onTrialExpiredClick }: HeaderAlertsProps) {
  const { nav } = useBusinessNav();
  const [trialDismissed] = useState(false);
  const [stripeDismissed] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Email verification logic
  const showVerifyEmail = user && !user.is_email_verified;

  // Legacy expired mobile subscriber
  const isLegacyMobileExpired = billingStatus &&
    billingStatus.is_legacy &&
    !billingStatus.migrated_at &&
    !billingStatus.is_active &&
    !billingStatus.has_stripe_subscription &&
    (billingStatus.subscription_source === 'play_store' || billingStatus.subscription_source === 'app_store');

  // Trial expired logic - no subscription, not trialing, and not active
  const isTrialExpired = billingStatus &&
    !billingStatus.has_stripe_subscription &&
    !billingStatus.is_trialing &&
    !billingStatus.is_active &&
    !isLegacyMobileExpired;

  // Trial banner logic (still in active trial)
  const showTrialBanner = !trialDismissed &&
    billingStatus &&
    !billingStatus.has_stripe_subscription &&
    billingStatus.days_until_trial_end !== null &&
    billingStatus.is_trialing;

  const daysLeft = billingStatus?.days_until_trial_end;
  const isUrgent = daysLeft != null && daysLeft <= 2;

  // Stripe banner logic - only show for paying customers (not on free trial)
  const isPaidCustomer = billingStatus?.has_stripe_subscription;
  const isPartiallyOnboarded = featureFlags?.stripe_account_id && !featureFlags?.payments_setup_complete;
  const isNotStarted = featureFlags && !featureFlags.stripe_account_id;
  const showStripeBanner = !stripeDismissed &&
    isPaidCustomer &&
    featureFlags &&
    !featureFlags.payments_setup_complete &&
    (isPartiallyOnboarded || isNotStarted);

  const handleStripeClick = async () => {
    if (!currentBusiness?.id || connecting) return;

    setConnecting(true);
    const response = await stripeApi.connect(currentBusiness.id);

    if (response.data?.onboarding_url) {
      window.location.href = response.data.onboarding_url;
    } else {
      setConnecting(false);
      nav('/settings/payments');
    }
  };

  if (!showTrialBanner && !showStripeBanner && !showVerifyEmail && !isTrialExpired && !isLegacyMobileExpired) {
    return null;
  }

  return (
    <>
    <div className="flex items-center gap-2">
      {showVerifyEmail && (
        <button
          onClick={() => setShowVerificationModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400 transition-all hover:scale-105"
        >
          <Mail className="size-3.5" />
          <span className="hidden lg:inline whitespace-nowrap">Verify Email</span>
        </button>
      )}

      {showTrialBanner && (
        <button
          onClick={() => nav('/settings/billing')}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105",
            isUrgent
              ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
          )}
        >
          {isUrgent ? <Clock className="size-3.5" /> : <Sparkles className="size-3.5" />}
          <span className="hidden lg:inline whitespace-nowrap">
            {`Free trial · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
          </span>
          <ChevronRight className="size-3 hidden lg:inline" />
        </button>
      )}

      {isTrialExpired && (
        <button
          onClick={onTrialExpiredClick ?? (() => nav('/settings/billing'))}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 transition-all hover:scale-105"
        >
          <Clock className="size-3.5" />
          <span className="hidden lg:inline whitespace-nowrap">Trial expired</span>
          <ChevronRight className="size-3 hidden lg:inline" />
        </button>
      )}

      {isLegacyMobileExpired && (
        <button
          onClick={() => nav('/settings/billing')}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 transition-all hover:scale-105"
        >
          <Smartphone className="size-3.5" />
          <span className="hidden lg:inline whitespace-nowrap">Subscription expired</span>
          <ChevronRight className="size-3 hidden lg:inline" />
        </button>
      )}

      {showStripeBanner && (
        <button
          onClick={handleStripeClick}
          disabled={connecting}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400 transition-all hover:scale-105 disabled:opacity-50"
        >
          {connecting ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <CreditCard className="size-3.5" />
          )}
          <span className="hidden lg:inline whitespace-nowrap">
            {connecting
              ? 'Connecting...'
              : isPartiallyOnboarded
                ? 'Finish Stripe setup'
                : 'Get Paid Faster'}
          </span>
          {!connecting && <ChevronRight className="size-3 hidden lg:inline" />}
        </button>
      )}
    </div>

    {user && (
      <EmailVerificationModal
        open={showVerificationModal}
        onOpenChange={setShowVerificationModal}
        email={user.email}
        onSuccess={onRefreshUser}
      />
    )}
    </>
  );
}
